import { Close } from "@mui/icons-material";
import { Box, Button, CircularProgress, CSSObject, IconButton, TextField, Tooltip } from "@mui/material";
import { useMemo, useState } from "react";
import { useMutation } from "react-query";
import DotLegalModal from "../../../common/modal/DotLegalModal";
import { Case, CaseType, RenewalStatus } from "../Case.types";
import { formatDate, updateRenewalStatuses } from "../CaseService";
import { BulkRenewalAction, getMaxSharedRemarkLength } from "./bulkRenewal.functions";

export type BulkRenewalModalProps = {
    bulkAction: BulkRenewalAction;
    selectedCases: Case[];
    caseType?: string;
    onClose: () => void;
    onCaseDeselected: (deselectedCase: Case) => void;
    onCasesUpdated: (updatedCases: Case[]) => void;
};

const getConfirmationText = (bulkAction: BulkRenewalAction, count: number, caseType?: string) => {
    const type = caseType === CaseType.Watches ? (count === 1 ? "watch" : "watches") : count === 1 ? "registration" : "registrations";
    return bulkAction === RenewalStatus.Renew
        ? `Are you sure you want to renew the following ${count} ${type}?`
        : `Are you sure you want to let the following ${count} ${type} expire?`;
};

const getBulkActionButtonText = (bulkAction: BulkRenewalAction, count: number) =>
    `${bulkAction === RenewalStatus.Renew ? "Renew" : "Expire"} ${count} ${count === 1 ? "case" : "cases"}`;

function BulkRenewalModal(props: BulkRenewalModalProps) {
    const [remark, setRemark] = useState("");
    const maxRemarkLength = useMemo(() => getMaxSharedRemarkLength(props.selectedCases), [props.selectedCases]);
    const saveStatusesMutation = useMutation(updateRenewalStatuses);

    const onConfirm = () => {
        saveStatusesMutation
            .mutateAsync({
                internalIds: props.selectedCases.map((x) => x.internalId),
                status: props.bulkAction,
                remark: remark,
            })
            .then((result) => props.onCasesUpdated(result.value()));
    };

    const content = (
        <Box>
            <Box color={(theme) => theme.palette.primary.main} textAlign={"center"} marginBottom={(theme) => theme.spacing(1)}>
                {getConfirmationText(props.bulkAction, props.selectedCases.length, props.caseType)}
            </Box>
            <Box maxHeight={280} overflow={"auto"} marginBottom={(theme) => theme.spacing(2)} color={"#6F7AC4"}>
                {props.selectedCases.map((x) => (
                    <Box
                        key={x.rowInternalId ?? x.internalId}
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        paddingY={0.5}
                        paddingX={1}
                        borderRadius={"8px"}
                        sx={{
                            "& .remove-case-button": { visibility: "hidden" },
                            "&:hover": { backgroundColor: "#FAF8F5" },
                            "&:hover .remove-case-button, &:focus-within .remove-case-button": { visibility: "visible" },
                        }}
                    >
                        <Box>
                            <Box component={"span"} fontWeight={"bold"}>
                                {x.name}
                                {x.country ? `, ${x.country}` : ""}
                            </Box>
                            {x.renewalDate ? `: ${formatDate(x.renewalDate)}` : ""}
                        </Box>
                        <Tooltip title={"Remove from selection"}>
                            <IconButton className={"remove-case-button"} size={"small"} onClick={() => props.onCaseDeselected(x)}>
                                <Close fontSize={"small"} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ))}
            </Box>
            <TextField
                value={remark}
                color={"primary"}
                onChange={(event) => setRemark(event.currentTarget.value)}
                label={"Remark"}
                placeholder={"Insert a remark..."}
                helperText={"The remark is added to the existing renewal remark on each selected case."}
                size={"medium"}
                rows={6}
                multiline
                inputProps={{ maxLength: maxRemarkLength }}
                sx={(theme) => ({ width: "100%" } as CSSObject)}
            />
            <Box marginTop={(theme) => theme.spacing(1)} color={(theme) => theme.customPalette.darkBlue}>{`${remark.length}/${maxRemarkLength}`}</Box>
        </Box>
    );

    return (
        <DotLegalModal
            open
            onClose={props.onClose}
            title={props.bulkAction === RenewalStatus.Renew ? "Bulk renew" : "Bulk expire"}
            titleToContentPadding={"slim"}
            content={content}
            action={
                saveStatusesMutation.isLoading ? (
                    <CircularProgress />
                ) : (
                    <Button color={"primary"} onClick={onConfirm} variant={"outlined"}>
                        {getBulkActionButtonText(props.bulkAction, props.selectedCases.length)}
                    </Button>
                )
            }
        />
    );
}

export default BulkRenewalModal;
